import * as React from 'react';
import { useLatestRef } from './use-latest-ref';

/** 'popup' takes Escape before anything inside or around it sees it; 'container' only once nothing inside handled it. */
export type DismissLayerKind = 'popup' | 'container';

export interface UseDismissLayerOptions {
    /** Registered while true, typically the layer's open state. */
    enabled: boolean;
    onDismiss: () => void;
    /** The layer's elements, such as a popup and its trigger. */
    getElements: () => ReadonlyArray<Element | null | undefined>;
    kind?: DismissLayerKind;
    /** A modal layer also takes Escape pressed outside every layer, such as on its backdrop or the body. */
    modal?: boolean;
}

export interface DismissLayer {
    options: React.MutableRefObject<UseDismissLayerOptions>;
    /** The layer this one renders inside in the React tree, which a portal hides from DOM containment. */
    parent: DismissLayer | undefined;
}

/** The enclosing layer in the React tree: a layer component renders its children inside this. */
export const DismissLayerContext = React.createContext<DismissLayer | undefined>(undefined);

// Inside a container, Escape on one of these belongs to a popup that is not a layer (a flowbite or MUI menu).
const UNREGISTERED_POPUP_SELECTOR =
    '[role="menu"], [role="dialog"], [aria-haspopup][aria-expanded="true"], [role="combobox"][aria-expanded="true"]';

const layers: DismissLayer[] = [];

const elementsOf = (layer: DismissLayer): Element[] =>
    layer.options.current.getElements().filter((element): element is Element => !!element);

const holds = (layer: DismissLayer, node: Node): boolean => elementsOf(layer).some((element) => element.contains(node));

const descendsFrom = (layer: DismissLayer, ancestor: DismissLayer): boolean =>
    !!layer.parent && (layer.parent === ancestor || descendsFrom(layer.parent, ancestor));

const encloses = (outer: DismissLayer, inner: DismissLayer): boolean =>
    descendsFrom(inner, outer) || elementsOf(inner).some((element) => holds(outer, element));

/** The newest layer, unless it encloses another: effects register children before parents. */
const topLayer = (): DismissLayer | undefined => {
    let top = layers[layers.length - 1];
    let moved = true;
    while (top && moved) {
        const current = top;
        const nested = layers.find((layer) => layer !== current && encloses(current, layer));
        moved = nested !== undefined;
        top = nested ?? current;
    }
    return top;
};

/** The topmost layer, when the Escape was pressed inside some layer or the topmost is modal. */
const ownerOf = (event: KeyboardEvent): DismissLayer | undefined => {
    if (event.key !== 'Escape' || event.isComposing || !(event.target instanceof Node)) {
        return undefined;
    }
    const top = topLayer();
    const target = event.target;
    const inside = layers.some((layer) => holds(layer, target));
    return top && (inside || top.options.current.modal) ? top : undefined;
};

const isInUnregisteredPopup = (layer: DismissLayer, target: Node): boolean => {
    const element = target instanceof Element ? target : target.parentElement;
    const popup = element?.closest(UNREGISTERED_POPUP_SELECTOR);
    return !!popup && !elementsOf(layer).some((own) => popup === own || popup.contains(own));
};

const dismiss = (event: KeyboardEvent, layer: DismissLayer): void => {
    event.preventDefault();
    event.stopPropagation();
    layer.options.current.onDismiss();
};

// Capture on window runs before React and floating-ui, so an enclosing dialog's own Escape never fires.
const handleCapture = (event: KeyboardEvent): void => {
    const owner = ownerOf(event);
    if (owner && (owner.options.current.kind ?? 'popup') === 'popup') {
        dismiss(event, owner);
    }
};

// Bubble on window runs last, so a control or unregistered popup that handled Escape keeps it.
const handleBubble = (event: KeyboardEvent): void => {
    const owner = ownerOf(event);
    if (
        owner &&
        owner.options.current.kind === 'container' &&
        !event.defaultPrevented &&
        !isInUnregisteredPopup(owner, event.target as Node)
    ) {
        dismiss(event, owner);
    }
};

const register = (layer: DismissLayer): (() => void) => {
    if (layers.length === 0) {
        window.addEventListener('keydown', handleCapture, true);
        window.addEventListener('keydown', handleBubble);
    }
    layers.push(layer);

    return () => {
        layers.splice(layers.indexOf(layer), 1);
        if (layers.length === 0) {
            window.removeEventListener('keydown', handleCapture, true);
            window.removeEventListener('keydown', handleBubble);
        }
    };
};

/** One shared Escape stack: only the topmost open layer closes, and the event goes no further. */
export const useDismissLayer = (options: UseDismissLayerOptions): DismissLayer => {
    const latest = useLatestRef(options);
    const parent = React.useContext(DismissLayerContext);
    const layer = React.useMemo(() => ({ options: latest, parent }), [latest, parent]);
    const { enabled } = options;

    React.useLayoutEffect(() => (enabled ? register(layer) : undefined), [enabled, layer]);

    return layer;
};
